<script lang="ts">
  import { createEventDispatcher, onDestroy, onMount, tick } from "svelte";
  import { cn } from "$lib/utils";
  import { Input } from "$lib/components/ui/input";
  import { debounce } from "$src/application/main/debounce";
  import { useSearchCustomers } from "../hooks/useSearchCustomer";
  import { get } from "svelte/store";
  import { type CustomerSummary } from "$src/domain/customers/CustomerSummary";
  import TimeSince from "$src/presentation/core/time-since.svelte";
  import { t } from "svelte-i18n";
  import AddressValue from "$src/presentation/core/address-value.svelte";
  import { getCustomerInfo } from "$src/domain/customers/utils/customer-info-utils";
  import CreateCustomerDialog from "./create-customer-dialog.svelte";
  import LoadingIcon from "$src/presentation/defaults/loading-icon.svelte";
  import { type Customer } from "$src/domain/customers/Customer";

  let container: HTMLElement;
  let listContainer: HTMLElement;

  export let groupStoreId: number;
  export let value: string = "";
  export let defaultCountryCode: string | undefined = undefined;

  let isOpen = false;

  let searchInput: Input;

  let searchValue: string = "";
  let selectedIndex = -1;
  let pageNumber = 1;
  let pageSize = 20;

  $: searchQuery = useSearchCustomers(
    groupStoreId,
    searchValue,
    pageNumber,
    pageSize
  );

  const dispatch = createEventDispatcher();

  function selectCustomer(customer: CustomerSummary) {
    value = getCustomerInfo(customer);
    dispatch("selectCustomer", customer);
    selectedIndex = -1;
    searchValue = value;
    isOpen = false;

    // patchHandler();
    // inputHandler();
  }

  function onCustomerCreated(event: CustomEvent<Customer>) {
    dispatch("selectCustomer", event.detail);
    isOpen = false;
  }

  const debouncedFetch = debounce((input: string) => {
    searchValue = value;
  }, 700);

  function handleInput() {
    isOpen = true;
    debouncedFetch(searchValue);

    // patchHandler();
    // inputHandler();
  }

  function showOptions(): void {
    searchValue = value;
    isOpen = true;
  }

  function handleClickOutside(event: MouseEvent) {
    if (container && !container.contains(event.target as Node)) {
      isOpen = false;
    }
  }

  async function handleKeydown(event: KeyboardEvent) {
    if (!showOptions) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      var query = get(searchQuery);
      if (query?.data) {
        selectedIndex = (selectedIndex + 1) % query.data.items.length;
        await tick();
        scrollToSelectedOption();
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();

      var query = get(searchQuery);
      if (query?.data) {
        selectedIndex =
          (selectedIndex - 1 + query.data.items.length) %
          query.data.items.length;
        await tick();
        scrollToSelectedOption();
      }
    } else if (event.key === "Enter" && selectedIndex >= 0) {
      event.preventDefault();
      var query = get(searchQuery);
      if (query?.data) {
        selectCustomer(query.data.items[selectedIndex]);
      }
    }
  }

  function scrollToSelectedOption() {
    const selectedElement = listContainer?.children[
      selectedIndex
    ] as HTMLElement;
    if (selectedElement) {
      const elementTop = selectedElement.offsetTop;
      const elementBottom = elementTop + selectedElement.offsetHeight;
      const containerScrollTop = listContainer.scrollTop;
      const containerHeight = listContainer.clientHeight;

      if (elementTop < containerScrollTop) {
        // Scroll up
        listContainer.scrollTop = elementTop;
      } else if (elementBottom > containerScrollTop + containerHeight) {
        // Scroll down
        listContainer.scrollTop = elementBottom - containerHeight;
      }
    }
  }

  onMount(() => {
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleKeydown);

    tick().then(() => searchInput.focus())
  });

  onDestroy(() => {
    document.removeEventListener("click", handleClickOutside);
    document.removeEventListener("keydown", handleKeydown);
  });
</script>

<div class="flex flex-col sm:flex-row gap-2">
  <div class="relative text-xs w-full" bind:this={container}>
    <Input
      
      bind:this={searchInput}
      bind:value
      placeholder={$t("page.customers.select.input")}
      on:input={handleInput}
      on:click={showOptions}
      on:focusin={showOptions}
    />
    {#if isOpen}
      <ul
        class="absolute w-[300px] mt-1 border bg-background rounded-lg shadow-lg max-h-44 overflow-auto z-10"
        bind:this={listContainer}
      >
        {#if $searchQuery.isSuccess}
          {#if $searchQuery.data.items.length > 0}
            {#each $searchQuery.data.items as customer, i}
              <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <li
                on:click={() => selectCustomer(customer)}
                class={cn(
                  "px-4 py-2 flex flex-row justify-between items-center border-b cursor-pointer hover:bg-secondary hover:text-secondary-foreground",
                  i == selectedIndex
                    ? "bg-secondary text-secondary-foreground"
                    : ""
                )}
              >
                <div class="flex-1 space-y-1">
                  <div class="flex w-full flex-col gap-1">
                    <div class="flex items-center">
                      <div class="flex items-center gap-2">
                        <div class="font-semibold">
                          {getCustomerInfo(customer)}
                        </div>
                      </div>
                      <div class="ml-auto text-xs text-muted-foreground">
                        <TimeSince dateString={customer.lastProjectDate} />
                      </div>
                    </div>
                  </div>
                  <div class="flex w-full flex-col gap-1">
                    <div class="flex items-center">
                      <div class="line-clamp-2 text-xs text-muted-foreground">
                        <AddressValue value={customer.billingAddress} />
                      </div>
                      <div class="ml-auto text-xs text-muted-foreground">
                        {customer.orderCount ?? 0}
                        {$t("page.search.results.sales")}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            {/each}
          {:else if searchValue.length > 0}
            <div class="p-2">
              {$t("page.search.results.noresult", {
                values: { query: searchValue },
              })}
            </div>
          {:else}
            <div class="p-2">
              {$t("page.customers.search.required", {
                values: { query: searchValue },
              })}
            </div>
          {/if}
        {:else if $searchQuery.isLoading}
            <LoadingIcon />
        {:else if $searchQuery.isError}
            {$searchQuery.error.message}
        {/if}
      </ul>
    {/if}
  </div>

  <CreateCustomerDialog {groupStoreId} {defaultCountryCode} on:customerCreated={onCustomerCreated} />
</div>
